import type { DeliveryDateOverride, DeliveryOverlay, RoundDayAmendment } from '@/types'

/** An overlay with nothing in it, for before it loads or if it fails. */
export const EMPTY_OVERLAY: DeliveryOverlay = {
  round_day_amendments: [],
  glass_windows: [],
  glass_opted_out: false,
  date_overrides: [],
  one_off_blackouts: [],
  schedule_banner: false,
}

/**
 * DF-473: the date customers are shown for a delivery (YYYY-MM-DD). Dynamics
 * keeps some deliveries on one date that reach the doorstep on another
 * (Christmas: Tuesday 22 delivered on Wednesday 23), set in the admin. Display
 * only: pauses, changes and cut-offs all use the Dynamics date.
 */
export const shownDateLookup = (overrides: DeliveryDateOverride[] = []) => {
  const map = new Map(overrides.map(o => [o.date, o.show_as]))
  return (date: string): string => map.get(date) ?? date
}

/** Whether a date (YYYY-MM-DD) is one one-off products can't be delivered on. */
export const inOneOffBlackout = (date: string, blackouts: { from: string, to: string }[] = []): boolean =>
  blackouts.some(b => date >= b.from && date <= b.to)

/** "17:00" as "5pm", "17:30" as "5:30pm". */
export const formatCutoffTime = (time = '17:00'): string => {
  const [h, m] = time.split(':').map(Number)
  const hour = h % 12 || 12
  return `${hour}${m ? `:${String(m).padStart(2, '0')}` : ''}${h < 12 ? 'am' : 'pm'}`
}

/**
 * Where each moved delivery actually arrives (YYYY-MM-DD), keyed by its own
 * date. Either Dynamics moved it to another day (Friday 25 to Tuesday 22), or
 * it stays on its Dynamics date but reaches the doorstep on another (Tuesday 22
 * on Wednesday 23), and a move can be both (Friday 25 arrives on Wednesday 23).
 * Pauses and changes always go on the delivery's own date (DF-473 rule 2).
 */
export const movedDeliveryTargets = (
  amendments: RoundDayAmendment[] = [],
  overrides: DeliveryDateOverride[] = []
): Map<string, string> => {
  const shownAs = shownDateLookup(overrides)
  const map = new Map(amendments.map(a => [a.date_from, shownAs(a.date_to)]))
  overrides.forEach(o => {
    if (!map.has(o.date)) map.set(o.date, o.show_as)
  })
  return map
}

/** A special delivery schedule period: whole weeks with a moved delivery. */
export interface SchedulePeriod {
  from: string
  to: string
  /** Every Monday in the period, for "weeks commencing 21 and 28 December". */
  weeks: string[]
  christmas: boolean
  label: string
}

const localDay = (key: string): Date => {
  const [y, m, d] = key.slice(0, 10).split('-').map(Number)
  return new Date(y, m - 1, d)
}

const dayKey = (date: Date): string =>
  `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`

const addDays = (key: string, days: number): string => {
  const d = localDay(key)
  d.setDate(d.getDate() + days)
  return dayKey(d)
}

const mondayOf = (key: string): string => {
  const d = localDay(key)
  d.setDate(d.getDate() - ((d.getDay() + 6) % 7))
  return dayKey(d)
}

/**
 * DF-478: the special schedule periods ("Christmas Delivery Schedule"). Every
 * week with a moved delivery in it, joined into runs of whole weeks, so they
 * follow the moves in Dynamics rather than fixed dates (Christmas 2026: the
 * weeks commencing 21 and 28 December).
 */
export const schedulePeriods = (targets: Map<string, string>): SchedulePeriod[] => {
  const mondays = new Set<string>()
  targets.forEach((to, from) => {
    mondays.add(mondayOf(from))
    mondays.add(mondayOf(to))
  })

  const periods: SchedulePeriod[] = []
  Array.from(mondays).sort().forEach(monday => {
    const last = periods[periods.length - 1]
    if (last && addDays(last.to, 1) === monday) {
      last.to = addDays(monday, 6)
      last.weeks.push(monday)
    } else {
      periods.push({ from: monday, to: addDays(monday, 6), weeks: [monday], christmas: false, label: '' })
    }
  })

  periods.forEach(p => {
    p.christmas = [p.from, p.to].some(d => `${d.slice(0, 4)}-12-25` >= p.from && `${d.slice(0, 4)}-12-25` <= p.to)
    p.label = p.christmas ? 'Christmas Delivery Schedule' : 'Changed Delivery Schedule'
  })
  return periods
}

/** The period a date (YYYY-MM-DD) falls in, if any. */
export const periodFor = (periods: SchedulePeriod[], key: string): SchedulePeriod | undefined =>
  periods.find(p => key >= p.from && key <= p.to)

/**
 * The message for changing a delivery on a moved date (DF-478): "Our Christmas
 * delivery schedule means this will be delivered on Wednesday 23rd December".
 * Null when the date isn't moved.
 */
export const movedDateNote = (
  key: string,
  targets: Map<string, string>,
  periods: SchedulePeriod[],
  formatDate: (key: string) => string,
  instead = false
): string | null => {
  const to = targets.get(key)
  if (!to) return null
  const schedule = periodFor(periods, key)?.christmas ? 'Our Christmas delivery schedule' : 'Our delivery schedule'
  return `${schedule} means this will be delivered on ${formatDate(to)}${instead ? ' instead' : ''}.`
}
